import React, { Suspense, useMemo, useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Typography,
  Divider,
  Card,
  CardContent,
  Alert,
  Snackbar,
  IconButton,
  Chip,
  Stack,
  Paper,
  Grid,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from "@mui/material";
import {
  VideoLibrary,
  Save,
  Edit,
  Schedule,
  LiveHelp,
  CheckCircle,
  ErrorOutline,
  ContentCopy,
  Add,
  History,
  Delete,
  EditCalendar
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import RichTextEditor from "./NotesRichTextEditor";
import { selectCurrentSuperAdmin } from "../../features/auth/authSelectors";
import { getCurrentUser, updateAdminData } from "../../features/auth/authSlice";
import { deleteFile } from "../../utils/deleteFile";
import { dataURLtoFile } from "../../utils/dataURLtoFile";
import { uploadFile } from "../../utils/uploadFile";
import dayjs from "dayjs";
import StickyHeadTable from "../Tables/QARequestTable";

const WeeklyQA = () => {
  const admin = useSelector(selectCurrentSuperAdmin);
  const [noteType, setNoteType] = useState("Gym");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newVideoUrl, setNewVideoUrl] = useState("");
  const [comments, setComments] = useState("");
  const [isSavingVideo, setIsSavingVideo] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    videoType: null
  });
  const [dateEditDialog, setDateEditDialog] = useState({
    open: false,
    videoType: null,
    selectedDate: null
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const [commentEditDialog, setCommentEditDialog] = useState({
    open: false,
    videoType: null,
    currentComment: ""
  });

  const handleEditComment = (videoType) => {
    const video =
      videoType === "currentWeek" ? currentWeekVideo : lastWeekVideo;
    setCommentEditDialog({
      open: true,
      videoType,
      currentComment: video.comments || ""
    });
  };

  const confirmCommentEdit = async () => {
    try {
      setIsUpdating(true);
      const { videoType, currentComment } = commentEditDialog;

      const updateData = {
        adminQALink: {
          ...admin?.adminQALink,
          [videoType]: {
            ...admin?.adminQALink?.[videoType],
            comments: currentComment.trim()
          }
        }
      };

      await dispatch(updateAdminData(updateData));
      setTimeout(() => dispatch(getCurrentUser()), 1500);

      showSnackbar("Comment updated successfully!", "success");
      setCommentEditDialog({
        open: false,
        videoType: null,
        currentComment: ""
      });
    } catch (err) {
      console.error(err);
      showSnackbar("Error updating comment", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const defaultValue = useMemo(
    () => admin?.adminNotes?.[noteType],
    [admin, noteType]
  );
  const [value, setValue] = useState("");
  const dispatch = useDispatch();
  const editorRef = useRef(null);

  // Get current and last week video data
  const currentWeekVideo = admin?.adminQALink?.currentWeek || {};
  const lastWeekVideo = admin?.adminQALink?.lastWeek || {};
  const hasCurrentWeek = Boolean(currentWeekVideo.url);
  const hasLastWeek = Boolean(lastWeekVideo.url);

  useEffect(() => {
    setValue(admin?.adminNotes?.[noteType] || "");
  }, [admin, noteType]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleAddVideo = async () => {
    try {
      if (isSavingVideo) return;

      // if (!newVideoUrl) {
      //   showSnackbar("Please enter a video URL", "error");
      //   return;
      // }

      setIsSavingVideo(true);

      await dispatch(
        updateAdminData({
          adminQALink: {
            url: newVideoUrl,
            ...(comments && { comments: comments.trim() }), // Remove leading/trailing spaces from comments,
            modifiedAt: dayjs.utc().format("YYYY-MM-DD")
          }
        })
      );

      setTimeout(() => dispatch(getCurrentUser()), 1500);
      setNewVideoUrl("");
      setComments("");
      showSnackbar("Video added successfully!");
    } catch (err) {
      console.error(err);
      showSnackbar("Error adding video", "error");
    } finally {
      setIsSavingVideo(false);
    }
  };

  const handleDeleteVideo = (videoType) => {
    setDeleteDialog({ open: true, videoType });
  };

  const confirmDeleteVideo = async () => {
    try {
      setIsUpdating(true);
      const { videoType } = deleteDialog;

      const updateData = {
        adminQALink: {
          ...admin?.adminQALink,
          [videoType]: {}
        }
      };

      await dispatch(updateAdminData(updateData));
      setTimeout(() => dispatch(getCurrentUser()), 1500);

      showSnackbar("Video deleted successfully!", "success");
      setDeleteDialog({ open: false, videoType: null });
    } catch (err) {
      console.error(err);
      showSnackbar("Error deleting video", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEditDate = (videoType) => {
    const video =
      videoType === "currentWeek" ? currentWeekVideo : lastWeekVideo;
    setDateEditDialog({
      open: true,
      videoType,
      selectedDate: video.modifiedAt ? dayjs.utc(video.modifiedAt) : dayjs()
    });
  };

  const confirmDateEdit = async () => {
    try {
      setIsUpdating(true);
      const { videoType, selectedDate } = dateEditDialog;

      const updateData = {
        adminQALink: {
          ...admin?.adminQALink,
          [videoType]: {
            ...admin?.adminQALink?.[videoType],
            modifiedAt: selectedDate.format("YYYY-MM-DD")
          }
        }
      };

      await dispatch(updateAdminData(updateData));
      setTimeout(() => dispatch(getCurrentUser()), 1500);

      showSnackbar("Upload date updated successfully!", "success");
      setDateEditDialog({ open: false, videoType: null, selectedDate: null });
    } catch (err) {
      console.error(err);
      showSnackbar("Error updating date", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSnackbar("URL copied to clipboard!");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not available";
    return `${dayjs
      .utc(dateString)
      .format("MMM DD, YYYY")} [ Users will see: (${dayjs
      .utc(dateString)
      .subtract(7, "days")
      .format("MMM DD, YYYY")} - ${dayjs
      .utc(dateString)
      .format("MMM DD, YYYY")}) ]`;
  };

  const VideoCard = ({
    title,
    video,
    icon,
    chipColor,
    chipLabel,
    videoType
  }) => (
    <Card elevation={2} sx={{ borderRadius: 2, height: "100%" }}>
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} mb={3}>
          {icon}
          <Typography variant="h6" fontWeight={600}>
            {title}
          </Typography>
          {/* <Chip
            label={chipLabel}
            color={chipColor}
            size="small"
            sx={{ fontWeight: 600 }}
          /> */}
        </Stack>

        {video.comments ? (
          <Stack spacing={3}>
            <TextField
              fullWidth
              value={video.url}
              size="medium"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Copy URL">
                      <IconButton
                        onClick={() => copyToClipboard(video.url)}
                        size="small"
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Upload Date">
                      <IconButton
                        onClick={() => handleEditDate(videoType)}
                        size="small"
                        color="primary"
                      >
                        <EditCalendar fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Video">
                      <IconButton
                        onClick={() => handleDeleteVideo(videoType)}
                        size="small"
                        color="error"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "#f8f9fa"
                }
              }}
            />

            <Typography variant="body2" color="text.secondary">
              Added: {formatDate(video.modifiedAt)}
            </Typography>

            {/* Enhanced Comments Section */}
            <Paper
              elevation={1}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0"
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb={1}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  color="text.primary"
                >
                  Comments
                </Typography>
                <Tooltip title="Edit Comments">
                  <IconButton
                    onClick={() => handleEditComment(videoType)}
                    size="small"
                    color="primary"
                    sx={{
                      bgcolor: "primary.main",
                      color: "white",
                      "&:hover": { bgcolor: "primary.dark" },
                      width: 28,
                      height: 28
                    }}
                  >
                    <Edit fontSize="14px" />
                  </IconButton>
                </Tooltip>
              </Stack>

              {video.comments ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    whiteSpace: "pre-line",
                    lineHeight: 1.6,
                    fontStyle: "italic"
                  }}
                >
                  "{video.comments}"
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  color="text.disabled"
                  sx={{
                    fontStyle: "italic",
                    textAlign: "center",
                    py: 1
                  }}
                >
                  No comments available
                </Typography>
              )}
            </Paper>
          </Stack>
        ) : (
          <Box
            sx={{
              p: 4,
              textAlign: "center",
              bgcolor: "#f8f9fa",
              borderRadius: 2,
              border: "2px dashed #dee2e6"
            }}
          >
            <VideoLibrary sx={{ fontSize: 48, color: "#6c757d", mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              No video added yet
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Grid container spacing={3}>
          {/* Add New Video Section */}
          <Grid item xs={12}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                  <Add color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    Add Announcements
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <TextField
                    fullWidth
                    placeholder="Enter video URL (YouTube, Vimeo, etc.)"
                    value={newVideoUrl}
                    onChange={(e) => setNewVideoUrl(e.target.value)}
                    size="medium"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddVideo}
                    disabled={isSavingVideo}
                    startIcon={
                      isSavingVideo ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <Add />
                      )
                    }
                    sx={{
                      py: 1.5,
                      px: 3,
                      borderRadius: 2,
                      fontWeight: 600,
                      textTransform: "none",
                      minWidth: 140,
                      background: "primary"
                    }}
                  >
                    {isSavingVideo ? "Adding..." : "Add"}
                  </Button>
                </Stack>
                <TextField
                  fullWidth
                  placeholder="Enter announcement comments"
                  value={comments}
                  multiline
                  rows={5}
                  onChange={(e) => setComments(e.target.value)}
                  size="medium"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2
                    },
                    marginTop: 4
                  }}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Current and Last Week Videos */}
          <Grid item xs={12} md={6}>
            <VideoCard
              title="Nutrition Video"
              video={currentWeekVideo}
              icon={<LiveHelp color="error" />}
              // chipColor="error"
              // chipLabel="CURRENT"
              videoType="currentWeek"
            />
          </Grid>

          {/* <Grid item xs={12} md={6}>
            <VideoCard
              title="Last Week Video"
              video={lastWeekVideo}
              icon={<History color="primary" />}
              chipColor="primary"
              chipLabel="PREVIOUS"
              videoType="lastWeek"
            />
          </Grid> */}
        </Grid>
        <StickyHeadTable />

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, videoType: null })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Delete color="error" />
              <Typography variant="h6">Delete Video</Typography>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this video? This action cannot be
              undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 1 }}>
            <Button
              onClick={() => setDeleteDialog({ open: false, videoType: null })}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDeleteVideo}
              color="error"
              variant="contained"
              disabled={isUpdating}
              startIcon={
                isUpdating ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Delete />
                )
              }
            >
              {isUpdating ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Date Edit Dialog */}
        <Dialog
          open={dateEditDialog.open}
          onClose={() =>
            setDateEditDialog({
              open: false,
              videoType: null,
              selectedDate: null
            })
          }
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={2}>
              <EditCalendar color="primary" />
              <Typography variant="h6">Edit Upload Date</Typography>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <DatePicker
              label="Upload Date"
              value={dateEditDialog.selectedDate}
              onChange={(newValue) =>
                setDateEditDialog((prev) => ({
                  ...prev,
                  selectedDate: newValue
                }))
              }
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: "normal"
                }
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 1 }}>
            <Button
              onClick={() =>
                setDateEditDialog({
                  open: false,
                  videoType: null,
                  selectedDate: null
                })
              }
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDateEdit}
              color="primary"
              variant="contained"
              disabled={isUpdating || !dateEditDialog.selectedDate}
              startIcon={
                isUpdating ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Save />
                )
              }
            >
              {isUpdating ? "Updating..." : "Update Date"}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={commentEditDialog.open}
          onClose={() =>
            setCommentEditDialog({
              open: false,
              videoType: null,
              currentComment: ""
            })
          }
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Edit color="primary" />
              <Typography variant="h6">Edit Comments</Typography>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              rows={8}
              label="Comments"
              placeholder="Add your comments here..."
              value={commentEditDialog.currentComment}
              onChange={(e) =>
                setCommentEditDialog((prev) => ({
                  ...prev,
                  currentComment: e.target.value
                }))
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2
                },
                mt: 2
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 1 }}>
            <Button
              onClick={() =>
                setCommentEditDialog({
                  open: false,
                  videoType: null,
                  currentComment: ""
                })
              }
              sx={{ textTransform: "none" }}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmCommentEdit}
              color="primary"
              variant="contained"
              disabled={isUpdating}
              startIcon={
                isUpdating ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Save />
                )
              }
              sx={{ textTransform: "none" }}
            >
              {isUpdating ? "Updating..." : "Update Comment"}
            </Button>
          </DialogActions>
        </Dialog>
        {/* Success/Error Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: "100%" }}
            icon={
              snackbar.severity === "success" ? (
                <CheckCircle />
              ) : (
                <ErrorOutline />
              )
            }
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default WeeklyQA;
